import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Fade,
  Zoom,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { CopyToClipboard } from "react-copy-to-clipboard";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import PetsIcon from "@mui/icons-material/Pets";
import ThunderstormIcon from "@mui/icons-material/Thunderstorm";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import ReactConfetti from "react-confetti";
import { keyframes } from "@emotion/react";
import dayjs from "dayjs";

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {transform: translateY(0);} 
  40% {transform: translateY(-20px);} 
  60% {transform: translateY(-10px);} 
`;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  height: "auto",
  minHeight: "28rem",
  width: isMobile ? "90%" : "500px",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 3,
  textAlign: "center",
  border: "none",
  outline: "none",
  "&:focus-visible": {
    outline: "none",
  },
  background: "linear-gradient(145deg, #ffffff, #f0f0f0)",
};

const animationContainer = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 300,
  height: "100%",
  gap: 2,
};

export const LuckyDrawModal = ({
  open,
  setOpen,
  promoPrizeAmount,
  promoCode,
  luckyDrawLoading,
  campaignSlug,
  voucherAnnouncementDate,
  campaign,
  joinedCampaign,
}) => {
  const [animationStage, setAnimationStage] = useState(0);
  const [copied, setCopied] = useState(false);
  const animationStages = ["sun", "horse", "rain"];
  const navigate = useNavigate();

  const now = dayjs();

  // Campaign period checks
  const campaignActive = campaign?.promo_start_date_inclusive && campaign?.promo_end_date_inclusive
    ? now.isAfter(dayjs(campaign.promo_start_date_inclusive)) && 
      now.isBefore(dayjs(campaign.promo_end_date_inclusive))
    : false;

  const campaignEnded = campaign?.promo_end_date_inclusive
    ? now.isAfter(dayjs(campaign.promo_end_date_inclusive))
    : false;

  // Voucher period checks
  const voucherNotStarted = campaign?.voucher_validity_start_date
    ? now.isBefore(dayjs(campaign.voucher_validity_start_date))
    : false;

  const voucherActive = 
    campaign?.voucher_validity_start_date && campaign?.voucher_validity_end_date
      ? now.isAfter(dayjs(campaign.voucher_validity_start_date)) &&
        now.isBefore(dayjs(campaign.voucher_validity_end_date))
      : false;

  useEffect(() => {
    if (!luckyDrawLoading) return;

    const interval = setInterval(() => {
      setAnimationStage((prev) => (prev + 1) % animationStages.length);
    }, 1000);

    return () => clearInterval(interval);
  }, [luckyDrawLoading]);

  const renderIcon = () => {
    const iconStyle = {
      fontSize: 120,
      color: "#FFD700",
      filter: "drop-shadow(0 0 8px rgba(255, 215, 0, 0.7))",
      animation: `${bounce} 2s infinite`,
    };

    switch (animationStages[animationStage]) {
      case "sun":
        return <EmojiEventsIcon sx={iconStyle} />;
      case "horse":
        return <MilitaryTechIcon sx={iconStyle} />;
      case "rain":
        return <CardGiftcardIcon sx={iconStyle} />;
      default:
        return <EmojiEventsIcon sx={iconStyle} />;
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClickCta = () => {
    setOpen(false);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="lucky-draw-modal"
        aria-describedby="lucky-draw-modal-description"
        sx={{
          zIndex: 9999,
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <Box sx={style}>
          {luckyDrawLoading ? (
            <Box sx={animationContainer}>
              <Fade in={true} timeout={500} key={animationStage}>
                <Box sx={{ textAlign: "center" }}>
                  {renderIcon()}
                  <Typography variant="h6" sx={{ mt: 2, fontWeight: 600, color: "#333" }}>
                    All the best!
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#666", mt: 1 }}>
                    Drawing your lucky prize...
                  </Typography>
                </Box>
              </Fade>
            </Box>
          ) : (
            // Render different content based on campaign state for EU GO I GO
            campaignSlug === "eu-go-i-go" ? (
              // EU GO I GO campaign - show different states
              <Box sx={{ textAlign: "center", py: 4 }}>
                {/* Campaign Active - Just joined */}
                {campaignActive && joinedCampaign && (
                  <>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                      You're In! 🎉
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#666", mb: 3 }}>
                      Thanks for joining the lucky draw!
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: "#f5f7fa",
                        p: 3,
                        borderRadius: "12px",
                        border: "1px solid #e4e8f0",
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        Winners Announced: {voucherAnnouncementDate}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#666" }}>
                        Check your app chat on this date to see if you've won! Vouchers will be valid from{" "}
                        {campaign?.voucher_validity_start_date && campaign?.voucher_validity_end_date
                          ? `${dayjs(campaign.voucher_validity_start_date).format("D MMM")} - ${dayjs(campaign.voucher_validity_end_date).format("D MMM")}`
                          : "17-31 Mar"}.
                      </Typography>
                    </Box>
                  </>
                )}

                {/* Campaign ended, waiting for voucher announcement */}
                {campaignEnded && voucherNotStarted && joinedCampaign && (
                  <>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                      Awaiting Results 🎲
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#666", mb: 3 }}>
                      You've joined the lucky draw!
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: "#fff3cd",
                        p: 3,
                        borderRadius: "12px",
                        border: "1px solid #ffc107",
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        Winners Announced: {voucherAnnouncementDate}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#666" }}>
                        Please wait until {voucherAnnouncementDate} to see if you've won! Check your app chat for the announcement.
                      </Typography>
                    </Box>
                  </>
                )}

                {/* Voucher period active */}
                {voucherActive && joinedCampaign && (
                  <>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                      Check Your App! 📱
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#666", mb: 3 }}>
                      Winners have been announced!
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: "#d4edda",
                        p: 3,
                        borderRadius: "12px",
                        border: "1px solid #28a745",
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        Vouchers Now Active
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#666" }}>
                        If you won, check your app chat for your voucher code! Vouchers are valid until{" "}
                        {campaign?.voucher_validity_end_date
                          ? dayjs(campaign.voucher_validity_end_date).format("D MMM, YYYY")
                          : "31 Mar, 2026"}.
                      </Typography>
                    </Box>
                  </>
                )}

                <Button
                  variant="contained"
                  onClick={() => setOpen(false)}
                  sx={{
                    fontFamily: "Manrope, sans-serif",
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: "white",
                    background: "linear-gradient(45deg, #4ECDC4, #45B7D1)",
                    textTransform: "none",
                    height: "3.5rem",
                    width: "100%",
                    maxWidth: "300px",
                    borderRadius: "12px",
                    mt: 3,
                    boxShadow: "0 4px 15px rgba(78, 205, 196, 0.4)",
                    "&:hover": {
                      background: "linear-gradient(45deg, #45B7D1, #4ECDC4)",
                      boxShadow: "0 6px 20px rgba(78, 205, 196, 0.6)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Got it!
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 3,
                  py: 4,
                }}
              >
                <ReactConfetti
                  recycle={false}
                  numberOfPieces={500}
                  gravity={0.2}
                  colors={["#FFD700", "#FFA500", "#FF6B6B", "#4ECDC4"]}
                />

                <Box sx={{ textAlign: "center", mb: 2 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      background: "linear-gradient(45deg, #FF6B6B, #4ECDC4)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      mb: 1,
                    }}
                  >
                    CONGRATULATIONS! 🎉
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#666", mb: 2 }}>
                    You've won an amazing prize!
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: "300px",
                    my: 2,
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)",
                      borderRadius: "12px",
                      p: 3,
                      boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                      border: "1px solid rgba(255,255,255,0.5)",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        bgcolor: "white",
                        p: "12px 20px",
                        borderRadius: "8px",
                        border: "1px dashed #4ECDC4",
                        mt: 1,
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          letterSpacing: "1px",
                          color: "#2C3E50",
                          textAlign: "center",
                          flex: 1,
                          fontSize: "14px",
                        }}
                      >
                        {promoCode}
                      </Typography>
                      <CopyToClipboard text={promoCode} onCopy={handleCopy}>
                        <IconButton
                          color="primary"
                          sx={{
                            color: "#4ECDC4",
                            "&:hover": {
                              backgroundColor: "rgba(78, 205, 196, 0.1)",
                            },
                          }}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </CopyToClipboard>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        fontSize: "0.9rem",
                        mt: 1,
                      }}
                    >
                      Worth{" "}
                      <span style={{ fontWeight: 700, color: "#2C3E50" }}>${promoPrizeAmount}</span>{" "}
                      off your next booking!
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      position: "absolute",
                      top: -10,
                      left: "50%",
                      transform: "translateX(-50%)",
                      bgcolor: "#4ECDC4",
                      color: "white",
                      px: 3,
                      py: 0.5,
                      borderRadius: "20px",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    PRIZE WON!
                  </Box>
                </Box>

                <CopyToClipboard text={promoCode} onCopy={handleCopy}>
                  <Button
                    variant="contained"
                    sx={{
                      fontFamily: "Manrope, sans-serif",
                      fontWeight: 700,
                      fontSize: "1rem",
                      color: "white",
                      background: "linear-gradient(45deg, #4ECDC4, #45B7D1)",
                      textTransform: "none",
                      height: "3.5rem",
                      width: "100%",
                      maxWidth: "300px",
                      borderRadius: "12px",
                      mt: 1,
                      mb: 1,
                      boxShadow: "0 4px 15px rgba(78, 205, 196, 0.4)",
                      "&:hover": {
                        background: "linear-gradient(45deg, #45B7D1, #4ECDC4)",
                        boxShadow: "0 6px 20px rgba(78, 205, 196, 0.6)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {copied ? "Copied!" : "Copy code"}
                  </Button>
                </CopyToClipboard>
              </Box>
            )
          )}
        </Box>
      </Modal>
    </>
  );
};

export default LuckyDrawModal;
